import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  DocumentTextIcon,
  FolderIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  TableCellsIcon,
  ChartBarIcon,
  BeakerIcon,
  CubeIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { FadeIn, StaggeredFadeIn } from '../../components/animations/FadeIn';
import {
  documentsApi,
  DOCUMENT_TYPE_LABELS,
  formatFileSize,
  type SiteDocument,
  type DocumentType,
  type DataImportResponse,
} from '../../api/documents';

// Document type icons — keys must match the backend DocumentType enum.
const DOCUMENT_TYPE_ICONS: Record<DocumentType, typeof DocumentIcon> = {
  agmr: DocumentTextIcon,
  gw_gradient: ChartBarIcon,
  gw_concentrations: AdjustmentsHorizontalIcon,
  om: DocumentIcon,
  product_recovery: CubeIcon,
  lab_report: BeakerIcon,
  other: FolderIcon,
};

// Document type colors
const DOCUMENT_TYPE_COLORS: Record<DocumentType, string> = {
  agmr: 'text-cyan-400 bg-cyan-500/20',
  gw_gradient: 'text-blue-400 bg-blue-500/20',
  gw_concentrations: 'text-purple-400 bg-purple-500/20',
  om: 'text-yellow-400 bg-yellow-500/20',
  product_recovery: 'text-pink-400 bg-pink-500/20',
  lab_report: 'text-green-400 bg-green-500/20',
  other: 'text-mono-400 bg-mono-500/20',
};

export function PolygonDocuments() {
  const { siteId } = useParams<{ siteId: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dataImportRef = useRef<HTMLInputElement>(null);

  // State
  const [documents, setDocuments] = useState<SiteDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<DocumentType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState<DocumentType>('other');
  const [uploadDate, setUploadDate] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');

  // Data import state
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<DataImportResponse | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);

  // Load documents
  const loadDocuments = useCallback(async () => {
    if (!siteId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await documentsApi.list(siteId, {
        document_type: filterType === 'all' ? undefined : filterType,
        page_size: 100,
      });
      setDocuments(response.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [siteId, filterType]);

  // Load on mount and filter change
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Reload when filter changes
  const handleFilterChange = (type: DocumentType | 'all') => {
    setFilterType(type);
    loadDocuments();
  };

  // File selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setShowUploadModal(true);
    }
  };

  // Upload document
  const handleUpload = async () => {
    if (!siteId || !selectedFile) return;

    setIsUploading(true);
    setUploadError(null);
    try {
      const result = await documentsApi.upload(
        siteId,
        selectedFile,
        uploadDocType,
        uploadDate || undefined,
        uploadDescription || undefined
      );
      if (result.success) {
        setUploadSuccess(`Uploaded: ${selectedFile.name}`);
        setShowUploadModal(false);
        setSelectedFile(null);
        setUploadDocType('other');
        setUploadDate('');
        setUploadDescription('');
        loadDocuments();
      } else {
        setUploadError(result.message);
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  // Download document
  const handleDownload = async (doc: SiteDocument) => {
    if (!siteId) return;
    try {
      await documentsApi.download(siteId, doc.id, doc.original_filename);
    } catch (err) {
      setError('Download failed');
    }
  };

  // Delete document
  const handleDelete = async (doc: SiteDocument) => {
    if (!siteId || !confirm(`Delete "${doc.original_filename}"?`)) return;

    try {
      await documentsApi.delete(siteId, doc.id);
      loadDocuments();
    } catch (err) {
      setError('Delete failed');
    }
  };

  // Data import
  const handleDataImportSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleDataImport(file);
    }
  };

  const handleDataImport = async (file: File) => {
    if (!siteId) return;

    setIsImporting(true);
    setImportResult(null);
    try {
      const result = await documentsApi.importGroundwaterData(siteId, file, true);
      setImportResult(result);
      setShowImportModal(true);
    } catch (err) {
      setImportResult({
        success: false,
        records_processed: 0,
        records_created: 0,
        records_updated: 0,
        wells_created: 0,
        errors: [{ row: 0, error: err instanceof Error ? err.message : 'Import failed' }],
        warnings: [],
      });
      setShowImportModal(true);
    } finally {
      setIsImporting(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      // Check if it's a data file (Excel/CSV) or a document
      const ext = file.name.toLowerCase().split('.').pop();
      if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
        // Show option to import or just upload
        if (confirm('Import this file as groundwater data? Click Cancel to upload as a document instead.')) {
          handleDataImport(file);
        } else {
          setSelectedFile(file);
          setShowUploadModal(true);
        }
      } else {
        setSelectedFile(file);
        setShowUploadModal(true);
      }
    }
  };

  // Filter documents by search (memoized to avoid recomputing on every render)
  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        doc.original_filename.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query) ||
        DOCUMENT_TYPE_LABELS[doc.document_type].toLowerCase().includes(query)
      );
    });
  }, [documents, searchQuery]);

  // Group documents by type for stats (memoized)
  const docsByType = useMemo(() => {
    return documents.reduce((acc, doc) => {
      acc[doc.document_type] = (acc[doc.document_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [documents]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <FadeIn>
        <div className="flex items-start justify-between">
          <div>
            <Link
              to={`/polygon/sites/${siteId}`}
              className="flex items-center text-mono-500 hover:text-mono-300 mb-2 text-xs tracking-wider"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              BACK TO SITE
            </Link>
            <h1 className="text-2xl font-bold text-mono-100 font-orbitron">Documents</h1>
            <p className="text-mono-500 text-sm mt-1">
              Upload and manage site documents, import groundwater data
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => dataImportRef.current?.click()}
              disabled={isImporting}
              className="flex items-center px-4 py-2 bg-green-500/20 border border-green-500/50 text-green-400 text-xs tracking-widest hover:bg-green-500/30 transition-colors disabled:opacity-50"
            >
              {isImporting ? (
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <TableCellsIcon className="h-4 w-4 mr-2" />
              )}
              IMPORT DATA
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center px-4 py-2 bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 text-xs tracking-widest hover:bg-cyan-500/30 transition-colors"
            >
              <CloudArrowUpIcon className="h-4 w-4 mr-2" />
              UPLOAD DOCUMENT
            </button>
          </div>
        </div>
      </FadeIn>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.gif,.tiff,.tif,.doc,.docx,.txt,.shp,.dbf,.shx,.prj,.zip"
        onChange={handleFileSelect}
      />
      <input
        ref={dataImportRef}
        type="file"
        className="hidden"
        accept=".xlsx,.xls,.csv"
        onChange={handleDataImportSelect}
      />

      {/* Success/Error Messages */}
      {uploadSuccess && (
        <FadeIn>
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3" />
            <span className="text-green-400 text-sm">{uploadSuccess}</span>
            <button
              onClick={() => setUploadSuccess(null)}
              className="ml-auto text-green-400 hover:text-green-300"
            >
              &times;
            </button>
          </div>
        </FadeIn>
      )}

      {error && (
        <FadeIn>
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3" />
            <span className="text-red-400 text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
              &times;
            </button>
          </div>
        </FadeIn>
      )}

      {/* Stats */}
      <StaggeredFadeIn className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" staggerDelay={30}>
        {Object.entries(DOCUMENT_TYPE_LABELS).map(([type, label]) => {
          const count = docsByType[type] || 0;
          const Icon = DOCUMENT_TYPE_ICONS[type as DocumentType];
          const colors = DOCUMENT_TYPE_COLORS[type as DocumentType];
          return (
            <button
              key={type}
              onClick={() => handleFilterChange(type as DocumentType)}
              className={`bg-mono-900 border rounded-lg p-3 text-left transition-colors ${
                filterType === type
                  ? 'border-cyan-500/50'
                  : 'border-mono-800 hover:border-mono-700'
              }`}
            >
              <div className={`inline-flex p-1.5 rounded ${colors} mb-2`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="text-xl font-bold text-mono-100 font-orbitron">{count}</div>
              <div className="text-xs text-mono-500 truncate">{label}</div>
            </button>
          );
        })}
      </StaggeredFadeIn>

      {/* Filter Bar */}
      <FadeIn delay={100}>
        <div className="flex items-center gap-4 bg-mono-900 border border-mono-800 rounded-lg p-4">
          <div className="flex items-center flex-1 bg-mono-950 rounded-lg px-3 py-2">
            <MagnifyingGlassIcon className="h-4 w-4 text-mono-500 mr-2" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent text-mono-100 text-sm flex-1 outline-none placeholder:text-mono-600"
            />
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-4 w-4 text-mono-500" />
            <select
              value={filterType}
              onChange={(e) => handleFilterChange(e.target.value as DocumentType | 'all')}
              className="bg-mono-950 text-mono-100 text-sm rounded-lg px-3 py-2 border border-mono-800 outline-none"
            >
              <option value="all">All Types</option>
              {Object.entries(DOCUMENT_TYPE_LABELS).map(([type, label]) => (
                <option key={type} value={type}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={loadDocuments}
            className="p-2 text-mono-500 hover:text-mono-300 transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </FadeIn>

      {/* Drop Zone */}
      <FadeIn delay={150}>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging
              ? 'border-cyan-500 bg-cyan-500/10'
              : 'border-mono-700 hover:border-mono-600'
          }`}
        >
          <CloudArrowUpIcon className={`h-12 w-12 mx-auto mb-4 ${isDragging ? 'text-cyan-400' : 'text-mono-600'}`} />
          <p className="text-mono-400 text-sm">
            Drag and drop files here, or{' '}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-cyan-400 hover:text-cyan-300"
            >
              browse
            </button>
          </p>
          <p className="text-mono-600 text-xs mt-2">
            PDF, Excel, CSV, Images, Word docs, GIS files (max 50MB)
          </p>
          <p className="text-green-400 text-xs mt-1">
            Drop Excel/CSV files to import groundwater data
          </p>
        </div>
      </FadeIn>

      {/* Document List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="h-8 w-8 text-cyan-500 animate-spin" />
        </div>
      ) : filteredDocuments.length === 0 ? (
        <FadeIn>
          <div className="text-center py-12 bg-mono-900 border border-mono-800 rounded-lg">
            <FolderIcon className="h-12 w-12 text-mono-600 mx-auto mb-4" />
            <p className="text-mono-500">No documents found</p>
            <p className="text-mono-600 text-sm mt-1">
              {filterType !== 'all' ? 'Try changing the filter or ' : ''}Upload your first document to get started
            </p>
          </div>
        </FadeIn>
      ) : (
        <StaggeredFadeIn className="space-y-2" staggerDelay={30}>
          {filteredDocuments.map((doc) => {
            const Icon = DOCUMENT_TYPE_ICONS[doc.document_type];
            const colors = DOCUMENT_TYPE_COLORS[doc.document_type];
            return (
              <div
                key={doc.id}
                className="bg-mono-900 border border-mono-800 rounded-lg p-4 flex items-center gap-4 hover:border-mono-700 transition-colors"
              >
                <div className={`p-2 rounded ${colors}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-mono-100 font-medium truncate">
                      {doc.original_filename}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${colors}`}>
                      {DOCUMENT_TYPE_LABELS[doc.document_type]}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-mono-500 mt-1">
                    <span>{formatFileSize(doc.file_size)}</span>
                    {doc.document_date && <span>{doc.document_date}</span>}
                    <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                  </div>
                  {doc.description && (
                    <p className="text-mono-500 text-xs mt-1 truncate">{doc.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 text-mono-500 hover:text-cyan-400 transition-colors"
                    title="Download"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(doc)}
                    className="p-2 text-mono-500 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </StaggeredFadeIn>
      )}

      {/* Upload Modal */}
      {showUploadModal && selectedFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <FadeIn>
            <div className="bg-mono-900 border border-mono-700 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-mono-100 mb-4 font-orbitron">Upload Document</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-mono-500 text-xs tracking-wider mb-1">FILE</label>
                  <div className="bg-mono-950 rounded-lg p-3 text-mono-300 text-sm truncate">
                    {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </div>
                </div>

                <div>
                  <label className="block text-mono-500 text-xs tracking-wider mb-1">DOCUMENT TYPE</label>
                  <select
                    value={uploadDocType}
                    onChange={(e) => setUploadDocType(e.target.value as DocumentType)}
                    className="w-full bg-mono-950 text-mono-100 rounded-lg px-3 py-2 border border-mono-800 outline-none"
                  >
                    {Object.entries(DOCUMENT_TYPE_LABELS).map(([type, label]) => (
                      <option key={type} value={type}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-mono-500 text-xs tracking-wider mb-1">
                    DOCUMENT DATE (optional)
                  </label>
                  <input
                    type="date"
                    value={uploadDate}
                    onChange={(e) => setUploadDate(e.target.value)}
                    className="w-full bg-mono-950 text-mono-100 rounded-lg px-3 py-2 border border-mono-800 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-mono-500 text-xs tracking-wider mb-1">
                    DESCRIPTION (optional)
                  </label>
                  <textarea
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    rows={2}
                    className="w-full bg-mono-950 text-mono-100 rounded-lg px-3 py-2 border border-mono-800 outline-none resize-none"
                    placeholder="Brief description..."
                  />
                </div>

                {uploadError && (
                  <div className="text-red-400 text-sm">{uploadError}</div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setUploadError(null);
                  }}
                  className="px-4 py-2 text-mono-400 hover:text-mono-200 text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="px-4 py-2 bg-cyan-500 text-mono-900 font-medium text-sm rounded hover:bg-cyan-400 transition-colors disabled:opacity-50"
                >
                  {isUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </FadeIn>
        </div>
      )}

      {/* Import Results Modal */}
      {showImportModal && importResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <FadeIn>
            <div className="bg-mono-900 border border-mono-700 rounded-lg p-6 w-full max-w-lg">
              <div className="flex items-center mb-4">
                {importResult.success ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-400 mr-3" />
                ) : (
                  <ExclamationTriangleIcon className="h-6 w-6 text-orange-400 mr-3" />
                )}
                <h3 className="text-lg font-semibold text-mono-100 font-orbitron">
                  {importResult.success ? 'Import Complete' : 'Import Completed with Issues'}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-mono-950 rounded-lg p-3">
                  <div className="text-mono-500 text-xs">Records Processed</div>
                  <div className="text-xl font-bold text-mono-100 font-orbitron">{importResult.records_processed}</div>
                </div>
                <div className="bg-mono-950 rounded-lg p-3">
                  <div className="text-mono-500 text-xs">Records Created</div>
                  <div className="text-xl font-bold text-green-400 font-orbitron">{importResult.records_created}</div>
                </div>
                <div className="bg-mono-950 rounded-lg p-3">
                  <div className="text-mono-500 text-xs">Records Updated</div>
                  <div className="text-xl font-bold text-cyan-400 font-orbitron">{importResult.records_updated}</div>
                </div>
                <div className="bg-mono-950 rounded-lg p-3">
                  <div className="text-mono-500 text-xs">Wells Created</div>
                  <div className="text-xl font-bold text-purple-400 font-orbitron">{importResult.wells_created}</div>
                </div>
              </div>

              {importResult.warnings.length > 0 && (
                <div className="mb-4">
                  <div className="text-mono-500 text-xs mb-2">Warnings</div>
                  <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3 max-h-24 overflow-y-auto">
                    {importResult.warnings.map((warn, i) => (
                      <div key={i} className="text-yellow-400 text-xs">{warn}</div>
                    ))}
                  </div>
                </div>
              )}

              {importResult.errors.length > 0 && (
                <div className="mb-4">
                  <div className="text-mono-500 text-xs mb-2">
                    Errors ({importResult.errors.length})
                  </div>
                  <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {importResult.errors.map((err, i) => (
                      <div key={i} className="text-red-400 text-xs">
                        Row {err.row}: {err.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 bg-cyan-500 text-mono-900 font-medium text-sm rounded hover:bg-cyan-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </FadeIn>
        </div>
      )}
    </div>
  );
}
